import React, {
  createContext,
  useContext,
} from "react";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import { useWebSocket } from "./WebSocketContext";
import { useToast } from "../components/ui/use-toast";
import { Message } from "@/types/index";

interface ChatContextState {
  messages: Message[];
  error: string | null;
  isSending: boolean;
}

interface ChatContextValue extends ChatContextState {
  sendMessage: (content: string) => Promise<void>;
}

interface ChatProviderProps {
  children: React.ReactNode;
  sessionId: string;
  documentId?: string;
}

const initialState: ChatContextState = {
  messages: [],
  error: null,
  isSending: false,
};

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export function ChatProvider({
  children,
  sessionId,
  documentId,
}: ChatProviderProps) {
  const { toast } = useToast();
  const { isConnected, sendMessage: wsSendMessage, error } = useWebSocket() || {};
  const { messages } = useAppSelector((state: RootState) => state.chat);

  // Função de envio usando o contexto global
  const sendMessage = async (content: string) => {
    if (!sessionId) {
      toast({
        title: "Error",
        description: "No active chat session",
        variant: "destructive",
      });
      return;
    }
    if (!isConnected) {
      toast({
        title: "Error",
        description: "Unable to send message. Connection lost.",
        variant: "destructive",
      });
      return;
    }
    try {
      wsSendMessage && wsSendMessage(content);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    }
  };

  return (
    <ChatContext.Provider
      value={{ messages, error: error || null, isSending: false, sendMessage }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
